/**
 * Shinohara Intensity Ratio Calculator
 *
 * Calculates two ratios that measure price strength:
 * - Weak Ratio: Measures intraday weakness (High-Close vs Close-Low)
 * - Strong Ratio: Measures interday strength (High-PrevClose vs PrevClose-Low)
 *
 * Algorithm (per ChartIQ lines 2328-2362):
 * 1. Maintain 4 rolling sums over Period:
 *    - weakNum = Sum(High - Close)
 *    - weakDen = Sum(Close - Low)
 *    - strongNum = Sum(High - PreviousClose)
 *    - strongDen = Sum(PreviousClose - Low)
 * 2. Calculate ratios:
 *    - Weak Ratio = 100 * weakNum / weakDen
 *    - Strong Ratio = 100 * strongNum / strongDen
 *
 * Key characteristics:
 * - Both ratios oscillate around 100
 * - Values > 100: Selling pressure dominates
 * - Values < 100: Buying pressure dominates
 * - Weak Ratio available after Period bars
 * - Strong Ratio available after Period + 1 bars (needs previous close)
 *
 * Reference: ChartIQ calculateShinohara (calcsChartIQ.js lines 2328-2362)
 */

import { isNotDefined, zipper } from "../utils";

const defaultOptions = {
  period: 26,
};

export default function shinohara() {
  let options = defaultOptions;

  function calculator(data) {
    const { period } = options;

    // Safety check for null/undefined data
    if (!data || data.length === 0) {
      return [];
    }

    // Initialize accumulators
    const accums = {
      weakNum: 0,
      weakDen: 0,
      strongNum: 0,
      strongDen: 0,
    };

    const weakRatio = [];
    const strongRatio = [];

    for (let i = 0; i < data.length; i++) {
      const d = data[i];

      // Check for required data
      if (
        isNotDefined(d.high) ||
        isNotDefined(d.low) ||
        isNotDefined(d.close)
      ) {
        weakRatio.push(undefined);
        strongRatio.push(undefined);
        continue;
      }

      // Accumulate current bar values
      accums.weakNum += d.high - d.close;
      accums.weakDen += d.close - d.low;

      // Strong ratio uses previous close (only if i > 0)
      if (i > 0) {
        const prevClose = data[i - 1].close;
        if (!isNotDefined(prevClose)) {
          accums.strongNum += d.high - prevClose;
          accums.strongDen += prevClose - d.low;
        }
      }

      // Start calculating ratios after period bars
      if (i >= period) {
        // Subtract oldest bar from window
        const oldBar = data[i - period];

        if (
          !isNotDefined(oldBar.high) &&
          !isNotDefined(oldBar.low) &&
          !isNotDefined(oldBar.close)
        ) {
          accums.weakNum -= oldBar.high - oldBar.close;
          accums.weakDen -= oldBar.close - oldBar.low;

          // Calculate Weak Ratio (avoid division by zero)
          if (accums.weakDen !== 0) {
            weakRatio.push((100 * accums.weakNum) / accums.weakDen);
          } else {
            weakRatio.push(100); // Default to 100 if denominator is zero
          }

          // Strong Ratio needs one more bar (uses i-1)
          if (i > period) {
            // Subtract oldest bar's contribution to strong ratio
            const oldBarPrev = data[i - period - 1];

            if (!isNotDefined(oldBarPrev.close)) {
              accums.strongNum -= oldBar.high - oldBarPrev.close;
              accums.strongDen -= oldBarPrev.close - oldBar.low;

              // Calculate Strong Ratio (avoid division by zero)
              if (accums.strongDen !== 0) {
                strongRatio.push((100 * accums.strongNum) / accums.strongDen);
              } else {
                strongRatio.push(100); // Default to 100 if denominator is zero
              }
            } else {
              strongRatio.push(undefined);
            }
          } else {
            strongRatio.push(undefined);
          }
        } else {
          weakRatio.push(undefined);
          strongRatio.push(undefined);
        }
      } else {
        weakRatio.push(undefined);
        strongRatio.push(undefined);
      }
    }

    // Use zipper to combine arrays into array of objects
    const zip = zipper().combine((weak, strong) => ({
      weakRatio: weak,
      strongRatio: strong,
    }));

    return zip(weakRatio, strongRatio);
  }

  /**
   * Returns the number of undefined values at the start
   */
  calculator.undefinedLength = function () {
    const { period } = options;
    // Strong Ratio needs period + 1 bars
    return period + 1;
  };

  /**
   * Gets or sets calculator options
   */
  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
